import * as React from 'react';
import type {FormAdapter} from '../useFormRegister';
import type {NativeTextFieldProps} from '../../TextField';

type TextFieldType = NativeTextFieldProps['type'];

type TextFieldFormValue = number | string | null;

const isReactEvent = (
  event: unknown,
): event is React.ChangeEvent<HTMLInputElement> => {
  return !!event && typeof event === 'object' && 'target' in event;
};

/**
 * A hook that provides adapter functions for TextField form integration.
 *
 * When type="number", the adapter automatically coerces values:
 * - Input values are stored as numbers in form state
 * - Empty input returns empty string (to avoid confusion with null which may
 *   have semantic meaning in some components for "field not applicable")
 * - Invalid input (e.g., "abc") keeps the previous value
 *
 * For other types, no adapters are returned and values remain as strings.
 */
export function useTextFieldFormAdapter(type?: TextFieldType): {
  valueAdapter?: FormAdapter<TextFieldFormValue, string>['valueAdapter'];
  changeAdapter?: FormAdapter<TextFieldFormValue, string>['changeAdapter'];
} {
  const valueAdapter = React.useCallback(
    (
      fieldValue: TextFieldFormValue,
      _controlValue?: TextFieldFormValue,
    ): string => {
      if (typeof fieldValue === 'number') {
        return String(fieldValue);
      }
      return fieldValue ?? '';
    },
    [],
  );

  const changeAdapter = React.useCallback(
    (
      event: React.ChangeEvent<HTMLElement> | string,
      fieldValue: TextFieldFormValue,
      _controlValue?: TextFieldFormValue,
    ): TextFieldFormValue => {
      if (!isReactEvent(event)) {
        return fieldValue;
      }

      const inputValue = event.target.value;

      // Empty string → empty string (not null, to avoid breaking components that
      // use null for conditional rendering like "doesn't support this field")
      if (inputValue === '') {
        return '';
      }

      // Try to use valueAsNumber if available (real DOM input), otherwise parse
      const num =
        'valueAsNumber' in event.target
          ? event.target.valueAsNumber
          : parseFloat(inputValue);

      if (Number.isNaN(num)) {
        // Invalid input - keep previous value
        return fieldValue;
      }

      return num;
    },
    [],
  );

  // Only return adapters for number type
  if (type === 'number') {
    return {valueAdapter, changeAdapter};
  }

  return {};
}
